import {
  C, combos, SLIDERS, type AudienceId, type ComponentOption, type Focus, type PartId, type ProblemId, type Profile,
} from "@bd/content";
import { priceIndex } from "./calendar";
import { BALANCE } from "./balance";
import { hashString } from "./rng";
import type { Design, FixMods } from "./state";
import { clamp } from "./util";
import { dpow } from "./dmath";

// ------------------------------------------------------------------ hidden fit matrix

/** Tags that sit well together (1.0 for the same tag). */
const RELATED: [string, string][] = [
  ["conflict", "tactical"], ["conflict", "history"], ["exploration", "story"], ["exploration", "science"], ["cozy", "puzzle"],
  ["cozy", "social"], ["economic", "puzzle"], ["economic", "tactical"], ["social", "silly"], ["social", "mystery"],
  ["mystery", "story"], ["mystery", "knowledge"], ["fantasy", "story"], ["fantasy", "exploration"], ["race", "speed"],
  ["knowledge", "history"], ["knowledge", "science"], ["speed", "silly"], ["classic", "cozy"], ["classic", "social"],
  ["history", "economic"], ["science", "puzzle"], ["fantasy", "conflict"], ["race", "exploration"],
];
const RELATED_SET = new Set(RELATED.flatMap(([a, b]) => [`${a}|${b}`, `${b}|${a}`]));

function tagAffinity(a: string, b: string): number {
  if (a === b) return 1;
  return RELATED_SET.has(`${a}|${b}`) ? 0.5 : 0;
}

const comboIndex = new Map(combos.map((c) => [`${c.mechanic}|${c.theme}`, c]));

export function perfectCombo(mechanic: string, theme: string, audience?: AudienceId) {
  const c = comboIndex.get(`${mechanic}|${theme}`);
  if (!c) return undefined;
  if (audience && c.audience !== audience) return undefined;
  return c;
}

const fitCache = new Map<string, number>();

/** Hidden mechanic × theme fit, 1..5. Fixed for every season so players can learn it. */
export function mechThemeFit(mechanic: string, theme: string): number {
  const key = `${mechanic}|${theme}`;
  const cached = fitCache.get(key);
  if (cached !== undefined) return cached;
  let fit: number;
  if (comboIndex.has(key)) fit = 5;
  else {
    const m = C.mechanic[mechanic];
    const t = C.theme[theme];
    let sim = 0;
    for (const mt of m.tags) {
      let best = 0;
      for (const tt of t.tags) best = Math.max(best, tagAffinity(mt, tt));
      sim += best;
    }
    sim /= m.tags.length;
    const h = hashString(`fit:${key}`) % 8; // 0..7: jitter -1 (2/8), 0 (4/8), +1 (2/8)
    const jitter = h < 2 ? -1 : h >= 6 ? 1 : 0;
    fit = Math.max(1, Math.min(4, Math.round(1 + 4 * sim) + jitter)); // 5 is reserved for perfect combos and very close matches
    if (sim >= 0.99 && jitter >= 0) fit = 5;
  }
  fitCache.set(key, fit);
  return fit;
}

export function mechAudienceFit(mechanic: string, audience: AudienceId): number {
  return C.mechanic[mechanic].audience[audience];
}

export function themeAudienceFit(theme: string, audience: AudienceId): number {
  return C.theme[theme].audience[audience];
}

export interface FitDetail {
  mt: number;
  ma: number;
  ta: number;
  formatOk: boolean;
  playersFactor: number;
  timeFactor: number;
  perfect?: string;
  F: number;
}

export function comboFit(d: Design): FitDetail {
  const mt = mechThemeFit(d.mechanic, d.theme);
  const ma = mechAudienceFit(d.mechanic, d.audience);
  const ta = themeAudienceFit(d.theme, d.audience);
  const mech = C.mechanic[d.mechanic];
  const aud = C.audience[d.audience];
  const formatOk = mech.formats.includes(d.format);
  const [amin, amax] = aud.players;
  const [pmin, pmax] = d.players;
  const overlap = Math.min(amax, pmax) - Math.max(amin, pmin);
  const playersFactor = overlap >= 1 ? 1 : overlap >= 0 ? 0.9 : 0.72;
  const timeRatio = d.playTime / aud.playTimeLimit;
  const timeFactor = timeRatio <= 1 ? 1 : clamp(1 - (timeRatio - 1) * 0.25, 0.55, 1);
  const perfect = perfectCombo(d.mechanic, d.theme, d.audience)?.name;
  const base = 0.45 * ((mt - 1) / 4) + 0.3 * ((ma - 1) / 4) + 0.25 * ((ta - 1) / 4);
  const F = clamp(base * (formatOk ? 1 : 0.65) * playersFactor * timeFactor + (perfect ? 0.08 : 0));
  return { mt, ma, ta, formatOk, playersFactor, timeFactor, perfect, F };
}

// ------------------------------------------------------------------ focus

export function idealFocus(mechanic: string, audience: AudienceId): Focus {
  const m = C.mechanic[mechanic].ideal;
  const a = C.audience[audience].ideal;
  const out = {} as Focus;
  for (const k of SLIDERS) out[k] = (m[k] + a[k]) / 2;
  return out;
}

export function focusSum(f: Focus): number {
  return SLIDERS.reduce((s, k) => s + f[k], 0);
}

/** 0..1, how close the sliders are to the hidden ideal shape (before execution quality). */
export function focusSimilarity(d: Design): number {
  const ideal = idealFocus(d.mechanic, d.audience);
  let dist = 0;
  for (const k of SLIDERS) dist += Math.abs(d.focus[k] - ideal[k]);
  return clamp(1 - dist / 14);
}

// ------------------------------------------------------------------ components, cost and value

export function partOption(d: Design, part: PartId): ComponentOption | undefined {
  const id = d.parts[part];
  return id ? C.component[id] : undefined;
}

export function partList(d: Design): ComponentOption[] {
  const out: ComponentOption[] = [];
  for (const id of Object.values(d.parts)) if (id && C.component[id]) out.push(C.component[id]);
  return out;
}

/** Scales content costs (listed as relative weights) to 1965 dollars per copy. */
export const COST_SCALE = 0.3;

/** Cost of making one copy, before the factory multiplier, in 1965 dollars. */
export function baseUnitCost(d: Design): number {
  const f = C.format[d.format];
  let cost = f.baseCost;
  for (const p of partList(d)) cost += p.cost;
  cost *= 1 + 0.03 * d.focus.components;
  // more players means more pieces in the box
  cost *= 0.85 + 0.05 * d.players[1];
  return cost * COST_SCALE;
}

export function copiesPerPallet(d: Design): number {
  return partOption(d, "box")?.perPallet ?? (d.format === "rpg_book" ? 400 : 300);
}

/** The game's size (filler, standard, big box, epic); older designs are standard. */
export function sizeOf(d: Pick<Design, "scope">) {
  return C.size[d.scope ?? "standard"] ?? C.size.standard;
}

/** What buyers feel the box is worth, in current dollars. */
export function fairPrice(d: Design, week: number, artQuality: number, reputation: number): number {
  const f = C.format[d.format];
  let parts = 0;
  for (const p of partList(d)) {
    parts += p.value;
    if (p.hobbyBoost && (d.audience === "hobby" || d.audience === "collectors" || d.audience === "hardcore")) parts += p.value * p.hobbyBoost;
  }
  // components matter more to some audiences, and each extra bit matters a little less
  const v0 = f.baseValue + BALANCE.partsValue * dpow(parts * BALANCE.componentTaste[d.audience], BALANCE.partsCurve);
  let v = v0;
  v *= 0.8 + 0.4 * artQuality;
  v *= 1 + 0.03 * d.focus.art + 0.04 * d.focus.components;
  v *= 0.9 + 0.3 * (reputation / 100);
  return v * 0.55 * priceIndex(week) * sizeOf(d).value;
}

/** The most this audience will pay; a bigger box is allowed to cost more. */
export function priceCeiling(audience: AudienceId, week: number, scope?: Design["scope"]): number {
  return C.audience[audience].priceCeiling * priceIndex(week) * (scope ? sizeOf({ scope }).ceiling : 1);
}

export interface ValueDetail {
  fair: number;
  ratio: number;
  overCeiling: boolean;
  V: number;
}

export function valueForMoney(d: Design, week: number, artQuality: number, reputation: number): ValueDetail {
  const fair = fairPrice(d, week, artQuality, reputation);
  const ratio = fair / Math.max(0.01, d.price);
  const overCeiling = d.price > priceCeiling(d.audience, week, d.scope) * 1.05;
  const V = clamp(0.65 + 0.7 * (ratio - 1) - (overCeiling ? 0.2 : 0));
  return { fair, ratio, overCeiling, V };
}

// ------------------------------------------------------------------ profile and problems

export interface Compiled extends Profile {
  avgPlayers: number;
  maxPlayers: number;
  clarity: number;
  simultaneous: boolean;
  naturalLen: number;
  length: number;
  endDrag: number;
  waitMin: number; // minutes each player waits per round
}

export function compile(d: Design, fixes: FixMods = {}, writingQuality = 0.4): Compiled {
  const m = C.mechanic[d.mechanic];
  const f = d.focus;
  const p: Profile = { ...m.profile };
  p.decisions += 0.45 * (f.depth - 2) - 0.2 * (f.access - 2) - 0.25 * f.speed;
  p.complexity += 0.06 * (f.depth - 2) - 0.07 * (f.access - 2);
  p.luck += -0.03 * (f.depth - 2);
  p.interaction += 0.07 * (f.interaction - 2);
  p.asymmetry += 0.04 * f.replay + 0.03 * f.immersion;
  p.powerSpread += 0.03 * f.replay;
  p.setup += 0.05 * f.components;
  p.turnMin *= (1 - 0.08 * f.speed) * (1 + 0.08 * (f.depth - 2));
  if (d.format === "rpg_book") p.complexity += 0.1;
  for (const part of partList(d)) p.setup += part.setup;
  for (const [k, v] of Object.entries(fixes)) if (k in p && v) (p as any)[k] += v;
  p.luck = clamp(p.luck);
  p.interaction = clamp(p.interaction);
  p.asymmetry = clamp(p.asymmetry);
  p.powerSpread = clamp(p.powerSpread);
  p.catchUp = clamp(p.catchUp);
  p.complexity = clamp(p.complexity);
  p.setup = clamp(p.setup);
  p.decisions = Math.max(0.5, p.decisions);
  p.turnMin = Math.max(0.1, p.turnMin);
  const avgPlayers = (d.players[0] + d.players[1]) / 2;
  const simultaneous = (fixes.simultaneous ?? 0) > 0 || d.mechanic === "real_time" || d.mechanic === "roll_write" || d.mechanic === "drafting";
  const clarity = clamp((partOption(d, "rulebook")?.clarity ?? 0.05) + 0.35 * writingQuality + (fixes.clarity ?? 0));
  const naturalLen = m.profile.lengthMin * dpow(avgPlayers / 3, 0.6) * (1 + 0.1 * (f.depth - 2)) * (1 - 0.05 * f.speed);
  const length = Math.max(naturalLen, d.playTime * 0.8);
  const endDrag = fixes.endDrag ?? 0;
  const waitMin = (avgPlayers - 1) * p.turnMin * (simultaneous ? 0.25 : 1);
  return { ...p, avgPlayers, maxPlayers: d.players[1], clarity, simultaneous, naturalLen, length, endDrag, waitMin };
}

const RULES_SENSITIVITY: Record<AudienceId, number> = {
  kids: 1.5, family: 1.2, party: 1.2, hobby: 0.9, hardcore: 0.6, collectors: 1, roleplayers: 0.8,
};

/** The true problems hiding in a design, severity 0..1. The table and reviewers find these. */
export function trueProblems(d: Design, c: Compiled): Record<ProblemId, number> {
  const aud = C.audience[d.audience];
  const hasCards = (d.parts.cards && d.parts.cards !== "cards_none") || d.format === "ccg" || d.format === "card";
  return {
    runaway: clamp((0.5 - c.catchUp) * 2 * (1 - c.luck)),
    kingmaker: c.maxPlayers >= 3 ? clamp((c.interaction - 0.6) * 2.5 * (1 - 0.5 * c.catchUp)) : 0,
    ap: clamp((c.decisions - 4.5) / 3),
    downtime: clamp((c.waitMin - 3) / 5),
    too_random: clamp(((c.luck - 0.5) / 0.3) * aud.luckTolerance),
    solved: clamp((0.3 - c.asymmetry - c.luck * 0.4 - d.focus.replay * 0.04 - c.powerSpread * 0.2) * 3),
    broken_card: hasCards ? clamp((c.powerSpread - 0.4) / 0.3) : 0,
    rules_confusion: clamp(((c.complexity - c.clarity) * 1.6 - 0.05) * RULES_SENSITIVITY[d.audience]),
    drags: clamp(0.9 * (c.length / aud.playTimeLimit - 1) + 0.5 * (c.length / c.naturalLen - 1.3) + c.endDrag),
    fiddly: clamp((c.setup - 0.5) / 0.4),
  };
}

export function fixMods(fixes: ProblemId[]): FixMods {
  const out: FixMods = {};
  for (const id of fixes) {
    const p = C.problem[id];
    for (const [k, v] of Object.entries(p.fixEffect)) (out as any)[k] = ((out as any)[k] ?? 0) + v;
  }
  return out;
}

/** Remaining problem load, 0..1, after fixes. P = 1 - this. */
export function problemLoad(probs: Record<ProblemId, number>): number {
  let s = 0;
  for (const v of Object.values(probs)) s += v;
  return clamp(s * BALANCE.problemWeight);
}

export interface ReplayInputs {
  F: number;
  M: number;
  designQuality: number;
  mastery: number;
  problems: Record<ProblemId, number>;
}

/** True replay intent, 0..1. 70% or more makes an evergreen. */
export function trueReplay(d: Design, c: Compiled, r: ReplayInputs): number {
  const w = BALANCE.replay;
  let replay = w.base + w.fit * r.F + w.focus * r.M + w.slider * d.focus.replay + w.asym * c.asymmetry + w.mastery * r.mastery + w.quality * r.designQuality;
  for (const p of partList(d)) replay += p.replay ?? 0;
  let sev = 0;
  for (const v of Object.values(r.problems)) sev += v;
  replay -= w.problems * sev;
  return clamp(replay, 0.05, 0.97);
}

// ------------------------------------------------------------------ validation

export interface DesignIssue {
  field: string;
  message: string;
}

export function validateDesign(d: Design, unlocked: (id: string) => boolean): DesignIssue[] {
  const out: DesignIssue[] = [];
  if (!d.name.trim()) out.push({ field: "name", message: "Give the game a name." });
  if (!C.mechanic[d.mechanic]) out.push({ field: "mechanic", message: "Unknown mechanic." });
  else if (!unlocked(`m:${d.mechanic}`)) out.push({ field: "mechanic", message: "That mechanic isn't unlocked yet." });
  if (!C.theme[d.theme]) out.push({ field: "theme", message: "Unknown theme." });
  else if (!unlocked(`t:${d.theme}`)) out.push({ field: "theme", message: "That theme isn't unlocked yet." });
  if (!C.audience[d.audience]) out.push({ field: "audience", message: "Unknown audience." });
  else if (!unlocked(`a:${d.audience}`)) out.push({ field: "audience", message: "That audience isn't unlocked yet." });
  if (!C.format[d.format]) out.push({ field: "format", message: "Unknown format." });
  else if (!unlocked(`f:${d.format}`)) out.push({ field: "format", message: "That format isn't unlocked yet." });
  const sum = focusSum(d.focus);
  if (Math.abs(sum - 10) > 1e-9) out.push({ field: "focus", message: `Spread exactly 10 focus points (now ${sum}).` });
  for (const k of SLIDERS) if (d.focus[k] < 0 || d.focus[k] > 5) out.push({ field: "focus", message: "Each slider runs from 0 to 5." });
  const fmt = C.format[d.format];
  if (fmt) {
    for (const [part, id] of Object.entries(d.parts)) {
      if (!id) continue;
      const opt = C.component[id];
      if (!opt || opt.part !== part) out.push({ field: `parts.${part}`, message: "Unknown component." });
      else if (!fmt.parts.includes(opt.part) && opt.tier > 0) out.push({ field: `parts.${part}`, message: `${fmt.name}s don't use ${part}.` });
      else if (opt.tier > 0 && !unlocked(`c:${id}`)) out.push({ field: `parts.${part}`, message: `${opt.name} isn't unlocked yet.` });
    }
    if (fmt.parts.includes("box") && !d.parts.box) out.push({ field: "parts.box", message: "Pick a box." });
    if (!d.parts.rulebook) out.push({ field: "parts.rulebook", message: "Pick a rulebook." });
  }
  const mech = C.mechanic[d.mechanic];
  if (mech?.requires?.includes("minis") && !(d.parts.minis && C.component[d.parts.minis]?.tier >= 2)) {
    out.push({ field: "parts.minis", message: "Miniature games need sculpted minis." });
  }
  if (d.players[0] < 1 || d.players[1] > 8 || d.players[0] > d.players[1]) out.push({ field: "players", message: "Players run from 1 to 8." });
  if (d.playTime < 10 || d.playTime > 240) out.push({ field: "playTime", message: "Play time runs from 10 to 240 minutes." });
  if (!(d.price > 0)) out.push({ field: "price", message: "Set a price." });
  return out;
}

export const NEUTRAL_FOCUS: Focus = { depth: 2, access: 2, immersion: 1, interaction: 1, replay: 1, art: 1, components: 1, speed: 1 };

/** Focus that exactly matches the hidden ideal (rounded to whole points). For tests and the post-release notebook only. */
export function idealFocusRounded(mechanic: string, audience: AudienceId): Focus {
  const ideal = idealFocus(mechanic, audience);
  const focus = {} as Focus;
  let left = 10;
  const order = [...SLIDERS].sort((a, b) => ideal[b] - ideal[a]);
  for (const k of SLIDERS) focus[k] = Math.floor(ideal[k]);
  for (const k of SLIDERS) left -= focus[k];
  for (const k of order) {
    if (left <= 0) break;
    if (ideal[k] - focus[k] > 0) {
      focus[k] += 1;
      left -= 1;
    }
  }
  for (let i = 0; left > 0; i++, left--) focus[order[i % order.length]] += 1;
  return focus;
}

/** A sensible starting design for a combo, used by the design desk and by bots. Focus starts neutral (the ideal is hidden). */
export function defaultDesign(
  name: string, mechanic: string, theme: string, audience: AudienceId, format: string, week: number, focus: Focus = NEUTRAL_FOCUS,
): Design {
  const aud = C.audience[audience];
  const fmt = C.format[format];
  const parts: Partial<Record<PartId, string>> = {};
  const cheapest = (part: PartId) => {
    const opts = Object.values(C.component).filter((c) => c.part === part && c.tier === 1);
    return opts[0]?.id;
  };
  for (const part of fmt.parts) {
    if (part === "minis" || part === "insert" || part === "extras" || part === "dice" || part === "board") parts[part] = `${part}_none`;
    else parts[part] = cheapest(part);
  }
  if (fmt.parts.includes("board") && format === "board") parts.board = "board_paper";
  if (format === "dice" || mechanic === "roll_move" || mechanic === "push_luck") parts.dice = "dice_standard";
  if (format === "board") parts.minis = "minis_plastic";
  if (fmt.parts.includes("box")) parts.box = "box_small";
  parts.rulebook = "rules_booklet";
  if (format === "card") parts.cards = "cards_thin";
  const m = C.mechanic[mechanic];
  const playTime = Math.min(aud.playTimeLimit, Math.round(m.profile.lengthMin / 5) * 5 || 30);
  const draft: Design = { name, mechanic, theme, audience, format, focus: { ...focus }, parts, players: [aud.players[0], Math.min(aud.players[1], 6)], playTime, price: 1 };
  draft.price = Math.round(Math.min(fairPrice(draft, week, 0.45, 30) * 1.05, priceCeiling(audience, week)) * 2) / 2 || 1;
  return draft;
}
